// VideoService.js - Version optimisée compatible avec la nouvelle architecture hybride
import { collection, doc, addDoc, getDocs, deleteDoc, updateDoc, query, where, orderBy, limit, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import sourceService from '../CP-Sourcing/SourceService';
import { askGPT } from '../components/OpenAIService';

class VideoServices {
  constructor() {
    this.cache = new Map();
    this.searchCache = new Map();
    this.cacheTimeout = 10 * 60 * 1000; // 10 minutes
    this.searchCacheTimeout = 30 * 60 * 1000; // 30 minutes

    // Configuration enrichie pour les thèmes
    this.videoThemes = {
      'entrepreneurship': {
        keywords: ['startup', 'entrepreneur', 'business', 'innovation', 'financement'],
        youtubeChannels: ['TEDxTalks', 'StanfordBusiness', 'HarvardBusiness'],
        searchTerms: 'startup entrepreneur business innovation formation tutoriel',
        categories: ['business', 'education', 'entrepreneurship'],
        discoveryTerms: 'startup entrepreneur business innovation financement pitch'
      },
      'technology': {
        keywords: ['programmation', 'développement', 'IA', 'tech', 'innovation'],
        youtubeChannels: ['TEDxTalks', 'GoogleDevelopers', 'Microsoft'],
        searchTerms: 'programmation développement IA tech formation tutoriel',
        categories: ['technology', 'programming', 'education'],
        discoveryTerms: 'programmation code développement IA machine learning'
      },
      'finance': {
        keywords: ['finance', 'investissement', 'trading', 'crypto', 'bourse'],
        youtubeChannels: ['TEDxTalks', 'Bloomberg', 'CNBC'],
        searchTerms: 'finance investissement trading formation tutoriel',
        categories: ['finance', 'business', 'investment'],
        discoveryTerms: 'finance investissement bourse trading crypto'
      },
      'leadership': {
        keywords: ['leadership', 'management', 'équipe', 'coaching'],
        youtubeChannels: ['TEDxTalks', 'HarvardBusiness', 'StanfordBusiness'],
        searchTerms: 'leadership management équipe coaching formation',
        categories: ['business', 'leadership', 'management'],
        discoveryTerms: 'leadership management équipe coaching motivation'
      },
      'health': {
        keywords: ['santé', 'bien-être', 'nutrition', 'fitness', 'yoga'],
        youtubeChannels: ['TEDxTalks', 'Yoga with Adriene', 'Fitness Blender'],
        searchTerms: 'santé bien-être nutrition fitness formation',
        categories: ['health', 'fitness', 'wellness'],
        discoveryTerms: 'santé bien-être nutrition fitness yoga sport'
      },
      'creativity': {
        keywords: ['créativité', 'design', 'art', 'photographie'],
        youtubeChannels: ['TEDxTalks', 'CreativeLive', 'Adobe'],
        searchTerms: 'créativité design art formation tutoriel',
        categories: ['arts', 'design', 'creativity'],
        discoveryTerms: 'créativité design art photographie dessin'
      }
    };
  }

  // ================================
  // MODE 1 : RECHERCHE PAR OBJECTIF (Nouveau système optimisé)
  // ================================

  /**
   * Récupérer les vidéos pré-analysées pour un objectif
   */
  async getVideosByObjective(objectiveId, userId, options = {}) {
    const { limit = 50 } = options;
    
    try {
      console.log(`📚 Récupération vidéos pour objectif: ${objectiveId}`);
      
      // Requête directe sur la table de liaison
      const q = query(
        collection(db, 'videos_by_objectives'),
        where('userId', '==', userId),
        where('objectiveId', '==', objectiveId),
        where('status', '==', 'active'),
        orderBy('semanticScore', 'desc'),
        limit(limit)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log('📭 Aucune vidéo pré-analysée pour cet objectif');
        return [];
      }

      // Récupérer les détails des vidéos
      const objectiveVideos = [];
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        const videoId = data.videoId;
        
        // Récupérer les détails de la vidéo
        const videoDoc = await this.getVideoById(videoId);
        if (videoDoc) {
          objectiveVideos.push({
            ...this.enhanceVideoData(videoDoc),
            semanticScore: data.semanticScore,
            semanticJustification: data.justification,
            analyzedAt: data.analyzedAt
          });
        }
      }

      console.log(`✅ ${objectiveVideos.length} vidéos trouvées pour l'objectif`);
      return objectiveVideos;

    } catch (error) {
      console.error('❌ Erreur récupération vidéos objectif:', error);
      return [];
    }
  }

  /**
   * Découvrir et analyser pour un objectif spécifique
   */
  async discoverAndAnalyzeForObjective(objective, userId, options = {}) {
    const { maxVideos = 30 } = options;
    
    try {
      console.log(`🔍 Découverte pour objectif: "${objective.title}"`);
      
      // 1. Découvrir de nouvelles vidéos
      const themeMapping = {
        'Développement entrepreneurial': 'entrepreneurship',
        'Formation et apprentissage': 'technology',
        'Finance et Banque': 'finance',
        'Evolution de carrière': 'leadership',
        'Santé et bien-être': 'health',
        'Activité ludique et créative': 'creativity'
      };
      
      const detectedTheme = themeMapping[objective.theme] || 'entrepreneurship';
      const discoveryResult = await this.discoverVideosByTheme(detectedTheme, userId, {
        maxVideos,
        searchQuery: objective.title
      });

      if (discoveryResult.added === 0) {
        console.log('⚠️ Aucune nouvelle vidéo découverte');
        return { success: false, added: 0, analyzed: 0 };
      }

      // 2. Analyser sémantiquement les nouvelles vidéos
      const newVideos = discoveryResult.videos || [];
      const analyzedCount = await this.analyzeVideosForObjective(newVideos, objective, userId);

      return {
        success: true,
        discovered: discoveryResult.discovered,
        added: discoveryResult.added,
        analyzed: analyzedCount
      };

    } catch (error) {
      console.error('❌ Erreur découverte/analyse objectif:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Analyser sémantiquement des vidéos pour un objectif
   */
  async analyzeVideosForObjective(videos, objective, userId) {
    if (!videos || videos.length === 0) return 0;
    
    try {
      console.log(`🧠 Analyse sémantique de ${videos.length} vidéos pour l'objectif`);
      
      const batchSize = 15; // Plus gros que l'ancien système
      let analyzedCount = 0;
      
      // Traiter par batches
      for (let i = 0; i < videos.length; i += batchSize) {
        const batch = videos.slice(i, i + batchSize);
        
        try {
          const scores = await this.scoreBatchForObjective(batch, objective);
          
          // Sauvegarder les résultats dans videos_by_objectives
          for (let j = 0; j < batch.length; j++) {
            const video = batch[j];
            const score = scores[j] || 0;
            
            if (score >= 4) { // Seuil minimum
              await addDoc(collection(db, 'videos_by_objectives'), {
                userId,
                objectiveId: objective.id,
                videoId: video.id,
                semanticScore: score,
                justification: `Score IA: ${score}/10 pour l'objectif "${objective.title}"`,
                analyzedAt: new Date().toISOString(),
                status: 'active'
              });
              analyzedCount++;
            }
          }
          
          // Délai entre batches
          if (i + batchSize < videos.length) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
        } catch (batchError) {
          console.warn(`⚠️ Erreur batch ${i}:`, batchError);
        }
      }
      
      console.log(`✅ ${analyzedCount} vidéos analysées et sauvegardées`);
      return analyzedCount;
      
    } catch (error) {
      console.error('❌ Erreur analyse sémantique:', error);
      return 0;
    }
  }

  /**
   * Scorer un batch de vidéos pour un objectif
   */
  async scoreBatchForObjective(videos, objective) {
    try {
      const videoContext = videos.map((video, index) => {
        return `[${index + 1}] "${video.title}"\nDescription: ${video.description || 'Pas de description'}\nChaîne: ${video.channelTitle || 'Inconnue'}`;
      }).join('\n\n');

      const prompt = `Évalue la pertinence de ces vidéos pour l'objectif: "${objective.title}"

VIDÉOS:
${videoContext}

Attribue un score de 0 à 10 pour chaque vidéo.
Réponds UNIQUEMENT en JSON: {"scores": [score1, score2, ...]}`;

      const response = await askGPT(prompt);
      
      try {
        const cleanResponse = response.replace(/```json|```/g, '').trim();
        const parsed = JSON.parse(cleanResponse);
        return parsed.scores || new Array(videos.length).fill(0);
      } catch (parseError) {
        // Fallback: extraction des nombres
        const matches = response.match(/\d+/g);
        return matches ? matches.map(n => parseInt(n)).slice(0, videos.length) : new Array(videos.length).fill(0);
      }
      
    } catch (error) {
      console.error('❌ Erreur scoring batch:', error);
      return new Array(videos.length).fill(0);
    }
  }

  // ================================
  // MODE 2 : RECHERCHE PAR THÈME (Optimisée)
  // ================================

  /**
   * Récupérer vidéos par thème depuis Firebase
   */
  async getVideosByTheme(theme, maxVideos = 50, userId = null) {
    try {
      const cacheKey = `videos_theme_${theme}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        console.log(`📦 Cache hit thème: ${theme}`);
        return cached.map(this.enhanceVideoData);
      }

      console.log(`🔍 Chargement vidéos thème: ${theme}`);

      const q = query(
        collection(db, 'videos'),
        where('theme', '==', theme),
        where('status', '==', 'active'),
        orderBy('addedAt', 'desc'),
        limit(maxVideos)
      );

      const querySnapshot = await getDocs(q);
      const videos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        source: 'firebase',
        ...doc.data()
      }));

      console.log(`✅ ${videos.length} vidéos thème ${theme}`);
      
      this.setCache(cacheKey, videos);
      return videos.map(this.enhanceVideoData);

    } catch (error) {
      console.error(`❌ Erreur thème ${theme}:`, error);
      return [];
    }
  }

  /**
   * Découvrir des vidéos par thème avec configuration enrichie
   */
  async discoverVideosByTheme(theme, userId, options = {}) {
    const { maxVideos = 20, searchQuery = null } = options;
    
    try {
      console.log(`🔍 Découverte thématique: ${theme}`);
      
      const themeConfig = this.videoThemes[theme];
      if (!themeConfig) {
        throw new Error(`Thème ${theme} non configuré`);
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non authentifié');
      
      const token = await user.getIdToken(true);

      // Construire la requête de recherche
      let searchTerms = searchQuery || themeConfig.discoveryTerms;
      if (searchQuery) {
        searchTerms = `${searchQuery} ${themeConfig.keywords.slice(0, 3).join(' ')}`;
      }

      // Recherche optimisée avec configuration thématique
      const searchResult = await sourceService.getRecommendedSources({
        userId: userId,
        userObjective: `Je recherche des vidéos éducatives récentes dans le domaine ${theme}. ${searchQuery ? `Focus sur: ${searchQuery}` : ''}`,
        query: searchTerms,
        languages: ['fr'],
        country: 'FR',
        maxSources: maxVideos,
        selectedProviders: ['YouTube', 'Google'],
        videoSearch: true,
        categories: themeConfig.categories,
        qualityFilter: 'high',
        timeRange: 'recent'
      }, token);

      if (!searchResult?.sources?.length) {
        return { discovered: 0, added: 0, videos: [] };
      }

      // Transformation et ajout
      const discoveredVideos = this.transformSourceToVideos(searchResult.sources, theme, searchQuery);
      const addedVideos = [];
      
      for (const video of discoveredVideos.slice(0, maxVideos)) {
        try {
          const exists = await this.checkVideoExists(video.url);
          if (!exists) {
            const videoId = await this.addVideoToDatabase(video, userId, theme);
            addedVideos.push({ ...video, id: videoId });
          }
        } catch (error) {
          console.warn('Erreur ajout vidéo:', error);
        }
      }

      this.clearCache(`videos_theme_${theme}`);
      
      return {
        discovered: discoveredVideos.length,
        added: addedVideos.length,
        videos: addedVideos
      };

    } catch (error) {
      console.error(`❌ Erreur découverte thème ${theme}:`, error);
      return { discovered: 0, added: 0, videos: [], error: error.message };
    }
  }

  // ================================
  // MODE 3 : RECHERCHE PAR SUJET LIBRE
  // ================================

  /**
   * Recherche hybride par mots-clés
   */
  async searchVideosByQuery(query, userId, options = {}) {
    const { maxResults = 30, useSemanticAnalysis = false } = options;
    
    try {
      console.log(`🔍 Recherche: "${query}"`);
      
      // Recherche textuelle immédiate
      const textualResults = await this.searchByKeywords(query, { limit: maxResults });
      
      if (!useSemanticAnalysis) {
        return {
          results: textualResults,
          type: 'textual',
          hasSemanticOption: true
        };
      }
      
      // Analyse sémantique optionnelle
      const semanticResults = await this.getSemanticResultsForQuery(query, userId, { maxResults });
      
      return {
        results: semanticResults,
        type: 'semantic',
        fallbackResults: textualResults
      };
      
    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      return { results: [], type: 'error', error: error.message };
    }
  }

  /**
   * Recherche simple par mots-clés
   */
  async searchByKeywords(query, options = {}) {
    const { limit = 30 } = options;
    
    try {
      // Recherche simple par mots-clés dans titre et description
      const keywords = query.toLowerCase().split(' ').filter(w => w.length > 2);
      
      // Récupérer toutes les vidéos actives (avec cache)
      const allVideos = await this.getAllVideos();
      
      // Filtrage textuel
      const filteredVideos = allVideos.filter(video => {
        const content = `${video.title} ${video.description}`.toLowerCase();
        return keywords.some(keyword => content.includes(keyword));
      });
      
      // Tri par pertinence simple
      const scoredVideos = filteredVideos.map(video => {
        const content = `${video.title} ${video.description}`.toLowerCase();
        let score = 0;
        
        keywords.forEach(keyword => {
          if (video.title.toLowerCase().includes(keyword)) score += 3;
          else if (content.includes(keyword)) score += 1;
        });
        
        return { ...video, textualScore: score };
      });
      
      return scoredVideos
        .sort((a, b) => b.textualScore - a.textualScore)
        .slice(0, limit);
      
    } catch (error) {
      console.error('❌ Erreur recherche textuelle:', error);
      return [];
    }
  }

  /**
   * Analyse sémantique pour requête libre
   */
  async getSemanticResultsForQuery(query, userId, options = {}) {
    const { maxResults = 30 } = options;
    
    try {
      // Vérifier le cache d'abord
      const cacheKey = this.hashString(`${userId}_${query}`);
      const cached = this.getFromSearchCache(cacheKey);
      if (cached) {
        console.log('📦 Cache hit recherche sémantique');
        return cached;
      }
      
      // Créer un objectif temporaire
      const tempObjective = {
        id: `temp_${Date.now()}`,
        title: query,
        theme: 'general',
        isTemporary: true
      };
      
      // Pré-filtrage des vidéos pertinentes
      const preFiltered = await this.searchByKeywords(query, { limit: 100 });
      
      if (preFiltered.length === 0) {
        return [];
      }
      
      // Analyse sémantique sur le sous-ensemble pré-filtré
      const analyzed = await this.analyzeVideosSubset(preFiltered, tempObjective);
      
      const results = analyzed
        .filter(video => video.semanticScore >= 4)
        .sort((a, b) => b.semanticScore - a.semanticScore)
        .slice(0, maxResults);
      
      // Cache pour 30 minutes
      this.setSearchCache(cacheKey, results);
      
      return results;
      
    } catch (error) {
      console.error('❌ Erreur analyse sémantique recherche:', error);
      return [];
    }
  }

  /**
   * Analyser un sous-ensemble de vidéos
   */
  async analyzeVideosSubset(videos, objective) {
    if (!videos || videos.length === 0) return [];
    
    try {
      const batchSize = 20;
      const analyzedVideos = [];
      
      for (let i = 0; i < videos.length; i += batchSize) {
        const batch = videos.slice(i, i + batchSize);
        const scores = await this.scoreBatchForObjective(batch, objective);
        
        batch.forEach((video, index) => {
          analyzedVideos.push({
            ...video,
            semanticScore: scores[index] || 0,
            semanticJustification: `Score IA pour "${objective.title}"`
          });
        });
        
        // Délai entre batches
        if (i + batchSize < videos.length) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
      }
      
      return analyzedVideos;
      
    } catch (error) {
      console.error('❌ Erreur analyse subset:', error);
      return videos.map(v => ({ ...v, semanticScore: 0 }));
    }
  }

  // ================================
  // MÉTHODES EXISTANTES CONSERVÉES
  // ================================

  /**
   * Récupérer TOUTES les vidéos
   */
  async getAllVideos() {
    try {
      console.log('🔍 VideoService - Récupération de toutes les vidéos');
      
      const cacheKey = 'all_videos';
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        console.log('📦 Cache hit pour toutes les vidéos');
        return cached;
      }

      const allVideos = [];
      const themes = Object.keys(this.videoThemes);
      
      for (const theme of themes) {
        try {
          const themeVideos = await this.getVideosByTheme(theme, 100);
          allVideos.push(...themeVideos);
        } catch (error) {
          console.warn(`⚠️ Erreur récupération thème ${theme}:`, error);
        }
      }
      
      console.log(`✅ VideoService - ${allVideos.length} vidéos récupérées au total`);
      
      this.setCache(cacheKey, allVideos);
      return allVideos;
      
    } catch (error) {
      console.error('❌ VideoService - Erreur getAllVideos:', error);
      return [];
    }
  }

  /**
   * Récupérer une vidéo par ID
   */
  async getVideoById(videoId) {
    try {
      const videoDoc = await getDocs(query(
        collection(db, 'videos'),
        where('__name__', '==', videoId),
        limit(1)
      ));
      
      if (!videoDoc.empty) {
        const doc = videoDoc.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.warn('Erreur récupération vidéo:', error);
      return null;
    }
  }

  /**
   * Transformer les sources en vidéos
   */
  transformSourceToVideos(sources, theme, searchQuery = null) {
    return sources.map((source, index) => {
      const baseTimestamp = Date.now() - (index * 60000);
      const uniqueId = `video_${theme}_${baseTimestamp}_${Math.random().toString(36).substr(2, 12)}_${index}`;
      
      return {
        id: uniqueId,
        title: source.title || 'Vidéo découverte',
        description: source.description || source.summary || '',
        url: source.usableUrl || source.recommendedUrl || source.url || '#',
        originalUrl: source.originalUrl || source.url || '',
        thumbnail: source.imageUrl || this.generateThumbnail(uniqueId),
        channelTitle: source.author || source.provider || 'Chaîne découverte',
        duration: source.duration || this.generateDuration(),
        provider: source.provider || 'sourceservice',
        viewCount: Number(source.viewCount) || Math.floor(Math.random() * 10000) + 1000,
        qualityScore: Number(source.qualityScore) || 6,
        publishedAt: source.publishedAt || new Date(baseTimestamp).toISOString(),
        theme: theme,
        language: source.language || 'fr',
        country: source.country || 'FR',
        isVerified: Boolean(source.isVerified) || true,
        searchQuery: searchQuery || null,
        source: 'sourceservice_discovery'
      };
    }).filter(video => {
      return video.title && video.url && video.url !== '#';
    });
  }

  /**
   * Vérifier si une vidéo existe déjà
   */
  async checkVideoExists(url) {
    try {
      const q = query(
        collection(db, 'videos'),
        where('url', '==', url),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.warn('Erreur vérification doublon vidéo:', error);
      return false;
    }
  }

  /**
   * Ajouter une vidéo à Firebase
   */
  async addVideoToDatabase(videoData, userId, theme) {
    try {
      const videoToAdd = {
        ...videoData,
        theme: theme,
        addedBy: userId,
        addedAt: new Date().toISOString(),
        status: 'active',
        totalViews: 0,
        discoveryMethod: videoData.discoveryMethod || 'sourceservice',
        lastUpdated: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'videos'), videoToAdd);
      console.log(`✅ Vidéo ajoutée: ${docRef.id}`);
      
      return docRef.id;
    } catch (error) {
      console.error('❌ Erreur ajout vidéo:', error);
      throw error;
    }
  }

  /**
   * Enregistrer une vue vidéo
   */
  async recordVideoView(videoId, userId = null, theme = null) {
    try {
      if (!userId || !videoId) return;

      console.log(`📺 Enregistrement vue vidéo: ${videoId}`);

      await addDoc(collection(db, 'video_views'), {
        videoId: String(videoId),
        userId: String(userId),
        theme: String(theme || ''),
        viewedAt: new Date().toISOString(),
        timestamp: Date.now()
      });

      const videoRef = doc(db, 'videos', String(videoId));
      await updateDoc(videoRef, {
        totalViews: increment(1),
        lastViewedAt: new Date().toISOString()
      });

      console.log(`✅ Vue enregistrée pour vidéo ${videoId}`);

    } catch (error) {
      console.warn('⚠️ Erreur enregistrement vue vidéo:', error);
    }
  }

  /**
   * Mettre à jour une vidéo
   */
  async updateVideo(videoId, updates, userId) {
    try {
      if (!userId) {
        throw new Error('Vous devez être connecté pour modifier une vidéo');
      }

      const videoRef = doc(db, 'videos', videoId);
      await updateDoc(videoRef, {
        ...updates,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: userId
      });
      
      console.log(`✅ Vidéo mise à jour: ${videoId}`);
      this.clearCache();
      return true;
    } catch (error) {
      console.error('❌ Erreur mise à jour vidéo:', error);
      throw error;
    }
  }

  /**
   * Supprimer une vidéo
   */
  async deleteVideo(videoId, userId) {
    try {
      if (!userId) {
        throw new Error('Vous devez être connecté pour supprimer une vidéo');
      }

      await deleteDoc(doc(db, 'videos', videoId));
      console.log(`✅ Vidéo supprimée: ${videoId}`);
      this.clearCache();
      return true;
    } catch (error) {
      console.error('❌ Erreur suppression vidéo:', error);
      throw error;
    }
  }

  // ================================
  // MÉTHODES STATISTIQUES (Conservées)
  // ================================

  /**
   * Générer des statistiques de découverte simulées réalistes
   */
  generateUserDiscoveryStats(userId) {
    if (!userId) return { watchedVideos: 0, totalWatchTime: 0, themesExplored: 0, discoveryStreak: 0 };
    
    // Utiliser l'userId comme seed pour des valeurs consistantes
    const seed = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    return {
      watchedVideos: Math.floor((seed % 12) + 3), // 3-14 vidéos découvertes
      totalWatchTime: Math.floor((seed % 80 + 20) * 60), // 20-99 minutes en secondes
      themesExplored: Math.floor((seed % 5) + 1), // 1-5 thèmes explorés
      discoveryStreak: Math.floor((seed % 7) + 1) // Série de découverte 1-7 jours
    };
  }

  /**
   * Récupérer les vidéos découvertes par l'utilisateur
   */
  async getUserWatchedVideos(userId) {
    try {
      if (!userId) {
        console.log('⚠️ getUserWatchedVideos: userId manquant');
        return [];
      }

      console.log(`🔍 Récupération historique de découverte pour: ${userId}`);
      
      const q = query(
        collection(db, 'video_views'),
        where('userId', '==', userId),
        orderBy('viewedAt', 'desc'),
        limit(50)
      );

      const querySnapshot = await getDocs(q);
      const watchedVideos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`✅ ${watchedVideos.length} vidéos découvertes récupérées depuis video_views`);
      return watchedVideos;

    } catch (error) {
      console.warn('⚠️ Collection video_views non accessible, utilisation de données simulées:', error.code);
      
      const simStats = this.generateUserDiscoveryStats(userId);
      const simulatedViews = [];
      
      for (let i = 0; i < simStats.watchedVideos; i++) {
        simulatedViews.push({
          id: `discovery_${i}`,
          videoId: `video_${i}`,
          viewedAt: new Date(Date.now() - (i * 12 * 60 * 60 * 1000)).toISOString(),
          watchDuration: Math.floor(Math.random() * 800 + 200),
          source: 'academy_discovery'
        });
      }
      
      return simulatedViews;
    }
  }

  /**
   * Statistiques globales
   */
  async getGlobalStats() {
    try {
      console.log('🔍 VideoService - Calcul des statistiques globales');
      
      const allVideos = await this.getAllVideos();
      
      const stats = {
        totalVideos: allVideos.length,
        totalViews: allVideos.reduce((sum, video) => sum + (video.viewCount || 0), 0),
        totalLikes: allVideos.reduce((sum, video) => sum + (video.likeCount || 0), 0),
        byTheme: {},
        byDiscoveryMethod: {
          backend: 0,
          manual: 0,
          youtube: 0
        },
        averagePopularity: 0,
        recentlyAdded: 0,
        themeDistribution: {}
      };
      
      // Analyser par thème et méthode de découverte
      allVideos.forEach(video => {
        const theme = video.theme || 'unknown';
        stats.byTheme[theme] = (stats.byTheme[theme] || 0) + 1;
        stats.themeDistribution[theme] = stats.byTheme[theme];
        
        if (video.discoveryMethod === 'sourceservice' || video.source === 'sourceservice_discovery') {
          stats.byDiscoveryMethod.backend++;
        } else if (video.source === 'youtube') {
          stats.byDiscoveryMethod.youtube++;
        } else {
          stats.byDiscoveryMethod.manual++;
        }
      });
      
      // Calculer la popularité moyenne
      if (allVideos.length > 0) {
        const totalPopularity = allVideos.reduce((sum, video) => sum + (video.popularityScore || 0), 0);
        stats.averagePopularity = Math.round(totalPopularity / allVideos.length * 10) / 10;
      }
      
      // Compter les ajouts récents (7 derniers jours)
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      stats.recentlyAdded = allVideos.filter(video => 
        video.addedAt && new Date(video.addedAt) > weekAgo
      ).length;
      
      console.log('✅ VideoService - Statistiques calculées:', stats);
      return stats;
      
    } catch (error) {
      console.error('❌ VideoService - Erreur getGlobalStats:', error);
      return {
        totalVideos: 0,
        totalViews: 0,
        totalLikes: 0,
        byTheme: {},
        byDiscoveryMethod: { backend: 0, manual: 0, youtube: 0 },
        averagePopularity: 0,
        recentlyAdded: 0,
        themeDistribution: {}
      };
    }
  }

  // ================================
  // UTILITAIRES ET AMÉLIORATION DES DONNÉES
  // ================================

  /**
   * Améliorer les données vidéo
   */
  enhanceVideoData = (video) => {
    const safeId = video.id || `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      ...video,
      id: String(safeId),
      title: String(video.title || 'Formation sans titre'),
      description: String(video.description || video.summary || ''),
      summary: String(video.summary || video.description || ''),
      url: String(video.url || '#'),
      usableUrl: String(video.usableUrl || video.url || '#'),
      originalUrl: String(video.originalUrl || video.url || '#'),
      thumbnail: String(video.thumbnail || this.generateThumbnail(safeId)),
      channelTitle: String(video.channelTitle || video.author || video.provider || 'Chaîne éducative'),
      duration: String(video.duration || this.generateDuration()),
      viewCount: Number(video.viewCount || video.totalViews || 0),
      qualityScore: Number(video.qualityScore || video.qualityRating || 5),
      qualityRating: Number(video.qualityRating || video.qualityScore || 5),
      popularityScore: Number(video.popularityScore || video.qualityScore || 5),
      
      // Champs sémantiques
      semanticScore: Number(video.semanticScore || 0),
      semanticJustification: String(video.semanticJustification || ''),
      semanticAnalysisDate: String(video.semanticAnalysisDate || ''),
      objectiveId: String(video.objectiveId || ''),
      objectiveTitle: String(video.objectiveTitle || ''),
      
      publishedAt: String(video.publishedAt || new Date().toISOString()),
      addedAt: String(video.addedAt || new Date().toISOString()),
      updatedAt: String(video.updatedAt || ''),
      lastViewedAt: String(video.lastViewedAt || ''),
      theme: String(video.theme || 'unknown'),
      language: String(video.language || 'fr'),
      source: String(video.source || 'firebase'),
      status: String(video.status || 'active'),
      discoveryMethod: String(video.discoveryMethod || 'manual'),
      searchQuery: String(video.searchQuery || ''),
      addedBy: String(video.addedBy || ''),
      isVerified: Boolean(video.isVerified !== false)
    };
  };

  /**
   * Générer une thumbnail cohérente
   */
  generateThumbnail(videoId) {
    const seed = videoId ? Math.abs(videoId.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) : Math.floor(Math.random() * 1000);
    return `https://picsum.photos/320/180?random=${seed}`;
  }

  /**
   * Générer une durée
   */
  generateDuration() {
    const minutes = Math.floor(Math.random() * 25 + 5);
    const seconds = Math.floor(Math.random() * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Formater le nombre de vues
   */
  formatViewCount = (viewCount) => {
    if (!viewCount && viewCount !== 0) return '0';
    
    const num = parseInt(viewCount);
    if (isNaN(num)) return '0';
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace('.0', '') + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1).replace('.0', '') + 'K';
    }
    
    return num.toLocaleString();
  };

  // ================================
  // GESTION CACHE
  // ================================

  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  getFromSearchCache(key) {
    const cached = this.searchCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.searchCacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setSearchCache(key, data) {
    this.searchCache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  clearCache(key = null) {
    if (key) {
      this.cache.delete(key);
      this.searchCache.delete(key);
    } else {
      this.cache.clear();
      this.searchCache.clear();
    }
    console.log('🗑️ Cache vidéo nettoyé');
  }

  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Informations sur le service
   */
  getServiceInfo() {
    return {
      cacheSize: this.cache.size,
      searchCacheSize: this.searchCache.size,
      cacheTimeout: this.cacheTimeout / 1000 / 60,
      searchCacheTimeout: this.searchCacheTimeout / 1000 / 60,
      themesConfigured: Object.keys(this.videoThemes).length,
      availableThemes: Object.keys(this.videoThemes),
      version: '3.0.0 - Hybrid Optimized Edition'
    };
  }
}

// Export singleton
const videoServices = new VideoServices();
export default videoServices;